/**
 * Beacon Post model for sovereignty mode stakes.
 * Ported from design_sheets/nav_beacons.html.
 */

import * as THREE from 'three';

// Module-level shared materials — tagged so per-instance dispose code skips them
const mMetal = new THREE.MeshLambertMaterial({ color: '#5a5a6a' });
const mMetalDk = new THREE.MeshLambertMaterial({ color: '#3a3a4a' });
mMetal.userData.shared = true;
mMetalDk.userData.shared = true;

function m(c: string): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ color: new THREE.Color(c) });
}

function mG(c: string): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ color: new THREE.Color(c), emissive: new THREE.Color(c), emissiveIntensity: 0.8 });
}

export interface BeaconModel {
  group: THREE.Group;
  beacon: THREE.Mesh | null;
  ptLight: THREE.PointLight | null;
}

export function buildBeaconPost(teamPrimary: string, teamAccent: string, destroyed: boolean): BeaconModel {
  const g = new THREE.Group();
  let beaconMesh: THREE.Mesh | null = null;
  const ptLight: THREE.PointLight | null = null;

  // --- GROUND STAKE ---
  const spike = new THREE.Mesh(new THREE.ConeGeometry(0.025, 0.1, 4), mMetalDk);
  spike.position.y = -0.02;
  spike.rotation.y = Math.PI / 4;
  g.add(spike);

  // Base plate
  const basePlate = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.07, 0.015, 6), mMetalDk);
  basePlate.position.y = 0.03;
  g.add(basePlate);

  // --- MAIN POST ---
  const postH = 0.45;
  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.022, postH, 6), m(teamAccent));
  post.position.y = 0.03 + postH / 2;
  if (destroyed) { post.rotation.z = 0.9; post.position.set(0.08, 0.12, 0.02); }
  g.add(post);

  // Team color bands
  const band = new THREE.Mesh(new THREE.CylinderGeometry(0.024, 0.024, 0.04, 6), m(teamPrimary));
  band.position.y = 0.15;
  if (destroyed) { band.position.set(0.05, 0.1, 0.02); band.rotation.z = 0.9; }
  g.add(band);

  const band2 = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.022, 0.025, 6), m(teamPrimary));
  band2.position.y = 0.35;
  if (destroyed) { band2.position.set(0.14, 0.14, 0.02); band2.rotation.z = 0.9; }
  g.add(band2);

  // --- ANTENNA TOP (planted only) ---
  if (!destroyed) {
    const antMast = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.008, 0.15, 4), mMetal);
    antMast.position.y = postH + 0.03 + 0.075;
    g.add(antMast);

    const cross1 = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.004, 0.004), mMetal);
    cross1.position.y = postH + 0.03 + 0.12;
    g.add(cross1);

    const cross2 = new THREE.Mesh(new THREE.BoxGeometry(0.004, 0.004, 0.08), mMetal);
    cross2.position.y = postH + 0.03 + 0.1;
    g.add(cross2);

    const tip = new THREE.Mesh(new THREE.SphereGeometry(0.008, 4, 4), mMetal);
    tip.position.y = postH + 0.03 + 0.155;
    g.add(tip);
  }

  // --- BEACON LIGHT ---
  const lightHousingY = destroyed ? 0.14 : postH + 0.01;
  const lightHousing = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.02, 0.03, 8), mMetalDk);
  lightHousing.position.y = lightHousingY;
  if (destroyed) { lightHousing.position.set(0.16, 0.12, 0.02); lightHousing.rotation.z = 0.9; }
  g.add(lightHousing);

  const beaconLight = new THREE.Mesh(
    new THREE.SphereGeometry(0.022, 8, 6),
    destroyed ? m('#2a2a2a') : mG(teamPrimary)
  );
  beaconLight.position.y = lightHousingY + 0.025;
  if (destroyed) { beaconLight.position.set(0.17, 0.14, 0.02); }
  g.add(beaconLight);
  if (!destroyed) beaconMesh = beaconLight;

  // No point light: the blink is the emissive on the beacon sphere (+ bloom).

  // --- GUY WIRES (planted only) ---
  if (!destroyed) {
    for (let i = 0; i < 3; i++) {
      const angle = (i / 3) * Math.PI * 2;
      const wire = new THREE.Mesh(
        new THREE.CylinderGeometry(0.002, 0.002, 0.35, 2),
        mMetalDk
      );
      wire.position.set(Math.cos(angle) * 0.06, 0.15, Math.sin(angle) * 0.06);
      wire.rotation.z = -Math.cos(angle) * 0.55;
      wire.rotation.x = Math.sin(angle) * 0.55;
      g.add(wire);

      const peg = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.004, 0.02, 4), mMetalDk);
      peg.position.set(Math.cos(angle) * 0.12, 0.01, Math.sin(angle) * 0.12);
      g.add(peg);
    }
  }

  // --- DEBRIS (destroyed only) ---
  if (destroyed) {
    const antBroken = new THREE.Mesh(new THREE.CylinderGeometry(0.005, 0.006, 0.08, 4), mMetal);
    antBroken.position.set(-0.1, 0.01, 0.05);
    antBroken.rotation.x = Math.PI / 2;
    antBroken.rotation.z = 0.3;
    g.add(antBroken);

    const crossBroken = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.004, 0.004), mMetal);
    crossBroken.position.set(0.05, 0.01, -0.08);
    crossBroken.rotation.y = 0.7;
    g.add(crossBroken);

    // Wire tangles (deterministic positions instead of random)
    const wirePosns = [{ x: -0.06, z: 0.04, ry: 0.8 }, { x: 0.07, z: -0.05, ry: 1.4 }];
    for (const wp of wirePosns) {
      const wire = new THREE.Mesh(new THREE.CylinderGeometry(0.002, 0.002, 0.12, 2), mMetalDk);
      wire.position.set(wp.x, 0.01, wp.z);
      wire.rotation.set(0.4, wp.ry, 0.6);
      g.add(wire);
    }
  }

  return { group: g, beacon: beaconMesh, ptLight };
}
